import { defineStore } from 'pinia';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { type LocationQueryRaw, useRoute, useRouter } from 'vue-router';
import { type AutocompleteResult, type GeoCoordinates, type LocationKind, PriceType } from '@/views/search/types.ts';
import type { CityId as APICityId, RegionId as APIRegionId } from '@/api';
import { getSearchFilters } from '@/api';

const defaultDistance = 100;
const searchLocationKinds: ReadonlySet<LocationKind> = new Set<LocationKind>(['city', 'region', 'location']);

export const salePriceRangeMin = 0;
export const salePriceRangeMax = 1_000_000;
const salePriceRangeStep = 5_000;
const salePriceRangeDistance = 50_000;

export const rentPriceRangeMin = 0;
export const rentPriceRangeMax = 5_000;
const rentPriceRangeStep = 100;
const rentPriceRangeDistance = 500;

const bedroomsRangeMin = 0;
const bedroomsRangeMax = 6;

const bathroomsRangeMin = 0;
const bathroomsRangeMax = 6;

export interface CategoryOption {
  id: number;
  text: string;
  parentId: number | null;
  child: boolean;
  checked: boolean;
}

function parsePositiveInteger(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parseQueryString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function hasQueryValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function parseCoordinates(value: unknown): GeoCoordinates | undefined {
  const coordinates = parseQueryString(value);
  if (!coordinates) return undefined;

  const parts = coordinates.split(',');
  if (parts.length !== 2) return undefined;

  const latitude = Number(parts[0]?.trim());
  const longitude = Number(parts[1]?.trim());

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;

  return { latitude, longitude };
}

function parseLocationKind(value: unknown): LocationKind | undefined {
  return typeof value === 'string' && searchLocationKinds.has(value as LocationKind) ? (value as LocationKind) : undefined;
}

function toCoordinatesQueryValue(coordinates: GeoCoordinates | undefined): string | undefined {
  if (!coordinates) return undefined;

  return `${coordinates.latitude},${coordinates.longitude}`;
}

export const useSearchFiltersStore = defineStore('searchFilters', () => {
  const router = useRouter();
  const route = useRoute();

  // Flag to prevent infinite navigation loops
  const isInitialized = ref(false);
  let isNavigating = false;
  let isSyncingFromRoute = false;

  // Location
  const autocomplete = ref<AutocompleteResult | null>(null);
  const regionId = ref<APIRegionId | undefined>(undefined);
  const cityId = ref<APICityId | undefined>(undefined);
  const distance = ref<number>(defaultDistance);

  // Price Type filters
  const priceType = ref<PriceType | undefined>(undefined);
  const hasPriceType = computed(() => priceType.value !== undefined);

  // Categories filter
  const categories = ref<CategoryOption[]>([]);
  const appliedCategories = computed(() => categories.value.filter((cat) => cat.checked));

  function clearCategory(categoryId: number) {
    const category = categories.value.find((c) => c.id === categoryId);
    if (category) category.checked = false;
  }

  function clearCategories() {
    categories.value.forEach((cat) => (cat.checked = false));
  }

  // Price filter
  const priceRange = computed(() => {
    return priceType.value == PriceType.ForRent
      ? {
          min: rentPriceRangeMin,
          max: rentPriceRangeMax,
          step: rentPriceRangeStep,
          distance: rentPriceRangeDistance,
        }
      : {
          min: salePriceRangeMin,
          max: salePriceRangeMax,
          step: salePriceRangeStep,
          distance: salePriceRangeDistance,
        };
  });

  const priceMin = ref<number>(priceType.value == PriceType.ForRent ? rentPriceRangeMin : salePriceRangeMin);
  const hasPriceMin = computed(() => priceMin.value !== priceRange.value.min);

  function clearPriceMin() {
    priceMin.value = priceRange.value.min;
  }

  const priceMax = ref<number>(priceType.value == PriceType.ForRent ? rentPriceRangeMax : salePriceRangeMax);
  const hasPriceMax = computed(() => priceMax.value !== priceRange.value.max);

  function clearPriceMax() {
    priceMax.value = priceRange.value.max;
  }

  function clearPriceType() {
    priceType.value = undefined;
    priceMin.value = salePriceRangeMin;
    priceMax.value = salePriceRangeMax;
  }

  // Bedroom filters
  const bedroomsMin = ref<number>(bedroomsRangeMin);
  const hasBedroomsMin = computed(() => bedroomsMin.value !== bedroomsRangeMin);

  function clearBedroomsMin() {
    bedroomsMin.value = bedroomsRangeMin;
  }

  const bedroomsMax = ref<number>(bedroomsRangeMax);
  const hasBedroomsMax = computed(() => bedroomsMax.value !== bedroomsRangeMax);

  function clearBedroomsMax() {
    bedroomsMax.value = bedroomsRangeMax;
  }

  // Bathroom filters
  const bathroomsMin = ref<number>(bathroomsRangeMin);
  const hasBathroomsMin = computed(() => bathroomsMin.value !== bathroomsRangeMin);

  function clearBathroomsMin() {
    bathroomsMin.value = bathroomsRangeMin;
  }

  const bathroomsMax = ref<number>(bathroomsRangeMax);
  const hasBathroomsMax = computed(() => bathroomsMax.value !== bathroomsRangeMax);

  function clearBathroomsMax() {
    bathroomsMax.value = bathroomsRangeMax;
  }

  // --- Navigation Methods ---
  async function navigateToSearch(force = false) {
    if (!force && isNavigating) return;

    isNavigating = true;
    try {
      const currentRoute = router.currentRoute.value;
      const query = toQuery({});

      if (currentRoute.name === 'search') {
        // If already on search page, just update query
        const currentQuery = JSON.stringify(currentRoute.query);
        const nextQuery = JSON.stringify(query);
        if (currentQuery !== nextQuery) {
          await router.replace({ name: 'search', query });
        }
      } else {
        // If on another page, navigate to search
        await router.push({ name: 'search', query });
      }
    } finally {
      isNavigating = false;
    }
  }

  // Update location from SearchBox
  function updateAutocomplete(result: AutocompleteResult) {
    autocomplete.value = result;
    regionId.value = result.location.regionId;
    cityId.value = result.location.cityId;
    distance.value = defaultDistance;
    void nextTick(() => navigateToSearch(true));
  }

  // --- Clear ALL Filters ---
  function clearAllFilters() {
    clearPriceType();
    clearCategories();
    clearPriceMin();
    clearPriceMax();
    clearBedroomsMin();
    clearBedroomsMax();
    clearBathroomsMin();
    clearBathroomsMax();
  }

  // Snapshot to use as a watch source
  const snapshot = computed(() => ({
    regionId: regionId.value,
    cityId: cityId.value,
    distance: distance.value,
    priceType: priceType.value,
    priceMin: priceMin.value,
    priceMax: priceMax.value,
    bedroomsMin: bedroomsMin.value,
    bedroomsMax: bedroomsMax.value,
    bathroomsMin: bathroomsMin.value,
    bathroomsMax: bathroomsMax.value,
    categoryChecks: categories.value.map((cat) => cat.checked),
  }));

  // Watch for any changes and automatically navigate
  watch(
    snapshot,
    async () => {
      if (isInitialized.value && !isNavigating && !isSyncingFromRoute) {
        await navigateToSearch();
      }
    },
    { deep: true }
  );

  // --- Hydration from Route/Query ---
  function fromQuery(query: LocationQueryRaw) {
    isSyncingFromRoute = true;

    try {
      const queryRegionId = parsePositiveInteger(query.regionId);
      if (regionId.value !== queryRegionId) {
        regionId.value = queryRegionId;
      }

      const queryCityId = parsePositiveInteger(query.cityId);
      if (cityId.value !== queryCityId) {
        cityId.value = queryCityId;
      }

      const queryLocationId = parsePositiveInteger(query.searchLocationId);
      const queryLocationName = parseQueryString(query.searchLocationName);
      const queryLocationTypeLabel = parseQueryString(query.searchLocationTypeLabel) ?? '';
      const queryCoordinates = parseCoordinates(query.location);
      const queryLocationKind = parseLocationKind(query.searchLocationKind) ?? (queryCityId ? 'city' : queryRegionId ? 'region' : queryCoordinates ? 'location' : undefined);

      if (queryLocationId && queryLocationName && queryLocationKind) {
        const nextAutocomplete: AutocompleteResult = {
          key: queryLocationId.toString(),
          location: {
            id: queryLocationId,
            name: queryLocationName,
            kind: queryLocationKind,
            typeLabel: queryLocationTypeLabel,
            coordinates: queryCoordinates,
            cityId: queryCityId,
            regionId: queryRegionId,
          },
        };

        if (JSON.stringify(autocomplete.value) !== JSON.stringify(nextAutocomplete)) {
          autocomplete.value = nextAutocomplete;
        }
      } else if (autocomplete.value !== null) {
        autocomplete.value = null;
      }

      if (query.distance) {
        const queryDistance = Number(query.distance);
        if (Number.isInteger(queryDistance) && queryDistance > 0) {
          if (distance.value !== queryDistance) {
            distance.value = queryDistance;
          }
        } else if (distance.value !== undefined) {
          distance.value = defaultDistance;
        }
      }

      const queryPriceType = parsePositiveInteger(query.priceTypeId);
      if (queryPriceType == PriceType.ForSale || queryPriceType == PriceType.ForRent) {
        if (priceType.value !== queryPriceType) {
          priceType.value = queryPriceType;
          priceMin.value = queryPriceType == PriceType.ForRent ? rentPriceRangeMin : salePriceRangeMin;
          priceMax.value = queryPriceType == PriceType.ForRent ? rentPriceRangeMax : salePriceRangeMax;
        }

        if (hasQueryValue(query.priceMin)) {
          const queryPriceMin = Number(query.priceMin);
          if (Number.isInteger(queryPriceMin) && queryPriceMin >= priceRange.value.min && queryPriceMin <= priceRange.value.max) {
            if (priceMin.value !== queryPriceMin) {
              priceMin.value = queryPriceMin;
            }
          } else if (priceMin.value !== priceRange.value.min) {
            priceMin.value = priceRange.value.min;
          }
        } else if (priceMin.value !== priceRange.value.min) {
          priceMin.value = priceRange.value.min;
        }

        if (hasQueryValue(query.priceMax)) {
          const queryPriceMax = Number(query.priceMax);
          if (Number.isInteger(queryPriceMax) && queryPriceMax >= priceRange.value.min && queryPriceMax <= priceRange.value.max) {
            if (priceMax.value !== queryPriceMax) {
              priceMax.value = queryPriceMax;
            }
          } else if (priceMax.value !== priceRange.value.max) {
            priceMax.value = priceRange.value.max;
          }
        } else if (priceMax.value !== priceRange.value.max) {
          priceMax.value = priceRange.value.max;
        }
      } else {
        clearPriceType();
      }

      const categoryIds = query.categoryIds ? ('' + query.categoryIds).split(',').map(Number) : [];
      categories.value.forEach((cat) => {
        const checked = categoryIds.includes(cat.id);
        if (cat.checked !== checked) {
          cat.checked = checked;
        }
      });

      if (query.bedroomsMin) {
        const queryBedroomsMin = Number(query.bedroomsMin);
        if (Number.isInteger(queryBedroomsMin) && queryBedroomsMin >= bedroomsRangeMin && queryBedroomsMin <= bedroomsRangeMax) {
          if (bedroomsMin.value !== queryBedroomsMin) {
            bedroomsMin.value = queryBedroomsMin;
          }
        } else if (bedroomsMin.value !== bedroomsRangeMin) {
          bedroomsMin.value = bedroomsRangeMin;
        }
      }

      if (query.bedroomsMax) {
        const queryBedroomsMax = Number(query.bedroomsMax);
        if (Number.isInteger(queryBedroomsMax) && queryBedroomsMax >= bedroomsRangeMin && queryBedroomsMax <= bedroomsRangeMax) {
          if (bedroomsMax.value !== queryBedroomsMax) {
            bedroomsMax.value = queryBedroomsMax;
          }
        } else if (bedroomsMax.value !== bedroomsRangeMax) {
          bedroomsMax.value = bedroomsRangeMax;
        }
      }

      if (query.bathroomsMin) {
        const queryBathroomsMin = Number(query.bathroomsMin);
        if (Number.isInteger(queryBathroomsMin) && queryBathroomsMin >= bathroomsRangeMin && queryBathroomsMin <= bathroomsRangeMax) {
          if (bathroomsMin.value !== queryBathroomsMin) {
            bathroomsMin.value = queryBathroomsMin;
          }
        } else if (bathroomsMin.value !== bathroomsRangeMin) {
          bathroomsMin.value = bathroomsRangeMin;
        }
      }

      if (query.bathroomsMax) {
        const queryBathroomsMax = Number(query.bathroomsMax);
        if (Number.isInteger(queryBathroomsMax) && queryBathroomsMax >= bathroomsRangeMin && queryBathroomsMax <= bathroomsRangeMax) {
          if (bathroomsMax.value !== queryBathroomsMax) {
            bathroomsMax.value = queryBathroomsMax;
          }
        } else if (bathroomsMax.value !== bathroomsRangeMax) {
          bathroomsMax.value = bathroomsRangeMax;
        }
      }
    } finally {
      void nextTick(() => {
        isSyncingFromRoute = false;
      });
    }
  }

  // --- Serialize to Route Query ---
  function toQuery(query: LocationQueryRaw): LocationQueryRaw | undefined {
    const newQuery = { ...query };
    let anySet = false;

    const location = autocomplete.value?.location;
    const locationQueryValue = toCoordinatesQueryValue(location?.coordinates);

    if (location && locationQueryValue) {
      if (newQuery.searchLocationId !== location.id) {
        newQuery.searchLocationId = location.id;
      }
      if (newQuery.searchLocationName !== location.name) {
        newQuery.searchLocationName = location.name;
      }
      if (newQuery.searchLocationTypeLabel !== location.typeLabel) {
        newQuery.searchLocationTypeLabel = location.typeLabel;
      }
      if (newQuery.searchLocationKind !== location.kind) {
        newQuery.searchLocationKind = location.kind;
      }
      if (newQuery.location !== locationQueryValue) {
        newQuery.location = locationQueryValue;
      }
      anySet = true;
    } else {
      delete newQuery.searchLocationId;
      delete newQuery.searchLocationName;
      delete newQuery.searchLocationTypeLabel;
      delete newQuery.searchLocationKind;
      delete newQuery.location;
    }

    // regionId / cityId
    if (cityId.value) {
      if (newQuery.cityId !== cityId.value) {
        newQuery.cityId = cityId.value;
      }
      delete newQuery.regionId;
      anySet = true;
    } else if (regionId.value) {
      if (newQuery.regionId !== regionId.value) {
        newQuery.regionId = regionId.value;
      }
      delete newQuery.cityId;
      anySet = true;
    } else {
      delete newQuery.regionId;
      delete newQuery.cityId;
    }

    if (distance.value !== undefined) {
      if (newQuery.distance !== distance.value) {
        newQuery.distance = distance.value;
      }
      anySet = true;
    } else {
      delete newQuery.distance;
    }

    // priceTypeId, priceMin, priceMax
    if (priceType.value !== undefined) {
      if (newQuery.priceTypeId !== priceType.value) {
        newQuery.priceTypeId = priceType.value;
      }
      anySet = true;

      if (priceMin.value !== priceRange.value.min) {
        if (newQuery.priceMin !== priceMin.value) {
          newQuery.priceMin = priceMin.value;
        }
        anySet = true;
      } else {
        delete newQuery.priceMin;
      }
      if (priceMax.value !== priceRange.value.max) {
        if (newQuery.priceMax !== priceMax.value) {
          newQuery.priceMax = priceMax.value;
        }
        anySet = true;
      } else {
        delete newQuery.priceMax;
      }
    } else {
      delete newQuery.priceTypeId;
      delete newQuery.priceMin;
      delete newQuery.priceMax;
    }

    // categoryIds
    if (appliedCategories.value.length) {
      const cats = appliedCategories.value.map((cat) => cat.id).join(',');
      if (newQuery.categoryIds !== cats) {
        newQuery.categoryIds = cats;
      }
      anySet = true;
    } else {
      delete newQuery.categoryIds;
    }

    // bedroomsMin
    if (bedroomsMin.value !== bedroomsRangeMin) {
      if (newQuery.bedroomsMin !== bedroomsMin.value) {
        newQuery.bedroomsMin = bedroomsMin.value;
      }
      anySet = true;
    } else {
      delete newQuery.bedroomsMin;
    }

    // bedroomsMax
    if (bedroomsMax.value !== bedroomsRangeMax) {
      if (newQuery.bedroomsMax !== bedroomsMax.value) {
        newQuery.bedroomsMax = bedroomsMax.value;
      }
      anySet = true;
    } else {
      delete newQuery.bedroomsMax;
    }

    // bathroomsMin
    if (bathroomsMin.value !== bathroomsRangeMin) {
      if (newQuery.bathroomsMin !== bathroomsMin.value) {
        newQuery.bathroomsMin = bathroomsMin.value;
      }
      anySet = true;
    } else {
      delete newQuery.bathroomsMin;
    }

    // bathroomsMax
    if (bathroomsMax.value !== bathroomsRangeMax) {
      if (newQuery.bathroomsMax !== bathroomsMax.value) {
        newQuery.bathroomsMax = bathroomsMax.value;
      }
      anySet = true;
    } else {
      delete newQuery.bathroomsMax;
    }

    return anySet ? newQuery : undefined;
  }

  onMounted(async () => {
    isSyncingFromRoute = true;
    const { data, error } = await getSearchFilters();
    if (error) {
      console.error('Could not fetch search filters:', error);
      return;
    }
    categories.value = data.categories.map((category) => ({
      id: category.id,
      text: category.plural,
      parentId: category.parentId,
      child: category.parentId != null && category.parentId > 0,
      checked: false,
    }));
    if (route.name === 'search') {
      fromQuery(route.query);
    }
    await nextTick(() => {
      isSyncingFromRoute = false;
      isInitialized.value = true;
    });
  });

  watch(
    () => route.fullPath,
    async () => {
      if (route.name === 'search') {
        fromQuery(route.query);
        return;
      } else if (route.name === 'home') {
        isNavigating = true;
        isSyncingFromRoute = true;
        clearAllFilters();
        await nextTick(() => {
          isNavigating = false;
          isSyncingFromRoute = false;
        });
      }
    }
  );

  return {
    regionId,
    cityId,
    distance,
    autocomplete,
    updateAutocomplete,

    priceType,
    hasPriceType,
    clearPriceType,

    categories,
    appliedCategories,
    clearCategory,
    clearCategories,

    priceRange,
    priceMin,
    hasPriceMin,
    clearPriceMin,

    priceMax,
    hasPriceMax,
    clearPriceMax,

    bedroomsRangeMin,
    bedroomsMin,
    hasBedroomsMin,
    clearBedroomsMin,

    bedroomsRangeMax,
    hasBedroomsMax,
    bedroomsMax,
    clearBedroomsMax,

    bathroomsRangeMin,
    bathroomsMin,
    hasBathroomsMin,
    clearBathroomsMin,

    bathroomsRangeMax,
    bathroomsMax,
    hasBathroomsMax,
    clearBathroomsMax,

    clearAllFilters,

    snapshot,
  };
});
